import { ShieldCheck, Wrench, CheckCircle, Circle, FilePdf, FolderOpen } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import AuthImage from "../AuthImage";
import { openAuthFile, downloadAuthFile } from "../../lib/fileOpen";
import { Button } from "../ui/button";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Steps({ steps, testid }) {
  return (
    <ul className="mt-2 space-y-1.5">
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-start gap-2 text-xs" data-testid={`${testid}-step-${i}`} data-done={s.done}>
          {s.done ? <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" weight="fill" /> : <Circle className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />}
          <span className={`flex-1 ${s.done ? "text-muted-foreground" : ""}`}>{s.label || s.text}{s.done && <span className="text-emerald-700 dark:text-emerald-400"> · {when(s.done_at)}{s.done_by ? ` by ${s.done_by}` : ""}</span>}</span>
          {s.photo && (
            <button type="button" onClick={() => openAuthFile(s.photo.url)} className="shrink-0 h-10 w-10 rounded border border-border overflow-hidden bg-muted" title="Open the evidence photo" data-testid={`${testid}-step-${i}-photo`}>
              <AuthImage path={s.photo.url} alt="Evidence" className="h-full w-full object-cover" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/* Office view of the engineer's make-safe + repair progress on an emergency job (read-only, with evidence photos and the PDF report). */
export default function MakeSafeProgress({ makeSafe, jobId, reference, testid = "emg-make-safe" }) {
  const steps = makeSafe?.steps || [];
  const repair = makeSafe?.repair || { steps: [], done: 0, total: 0 };
  if (!steps.length && !repair.steps.length) return null;
  const allSafe = steps.length > 0 && makeSafe.done === makeSafe.total;
  const allFixed = repair.total > 0 && repair.done === repair.total;
  return (
    <div className="space-y-2">
      {steps.length > 0 && (
        <div className={`rounded-lg border px-4 py-3 ${allSafe ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20" : "border-border bg-card"}`} data-testid={testid} data-done={makeSafe.done} data-total={makeSafe.total}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" weight="fill" /> Make-safe steps on site</p>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${allSafe ? "bg-emerald-600 text-white" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`} data-testid={`${testid}-progress`}>
                {allSafe ? `Made safe ${when(makeSafe.made_safe_at)}` : `${makeSafe.done} of ${makeSafe.total} confirmed by the engineer`}
              </span>
              {allSafe && jobId && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadAuthFile(`/api/emergency-jobs/${jobId}/made-safe.pdf`, `${reference || "emergency"}_Made-Safe-Report.pdf`)} data-testid={`${testid}-pdf-btn`}>
                  <FilePdf className="h-3.5 w-3.5 mr-1" /> Made Safe report
                </Button>
              )}
            </div>
          </div>
          {makeSafe.report_file && (
            <Link to={`/files/${makeSafe.report_file.folder_id}`} className="mt-1 text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid={`${testid}-hub-link`}>
              <FolderOpen className="h-3.5 w-3.5" /> Filed in the File Hub · {makeSafe.report_file.folder_path || makeSafe.report_file.name}
            </Link>
          )}
          <Steps steps={steps} testid={testid} />
        </div>
      )}
      {repair.steps.length > 0 && (
        <div className={`rounded-lg border px-4 py-3 ${allFixed ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20" : "border-border bg-card"}`} data-testid={`${testid}-repair`} data-done={repair.done} data-total={repair.total}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold flex items-center gap-2"><Wrench className="h-4 w-4" weight="fill" /> Repair steps on site</p>
            <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${allFixed ? "bg-emerald-600 text-white" : "bg-primary/15 text-primary"}`} data-testid={`${testid}-repair-progress`}>
              {allFixed ? `Repair complete ${when(repair.completed_at)}` : `${repair.done} of ${repair.total} done`}
            </span>
          </div>
          <Steps steps={repair.steps} testid={`${testid}-repair`} />
        </div>
      )}
    </div>
  );
}
